import React, { Component } from 'react';
import AthleticColorChart from './AthleticColorChart';
import './JerseyM54.css';

const MAX_NAMES = 3;

const styles = {
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: '18px',
    },
    sectionLabel: {
        fontSize: '11px',
        color: '#888',
        textTransform: 'uppercase',
        letterSpacing: '1px',
        marginBottom: '8px',
    },
    addBtn: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        width: '100%',
        padding: '9px 0',
        background: '#1a1a1a',
        border: '2px dashed #444',
        borderRadius: '6px',
        color: '#aaa',
        fontSize: '12px',
        cursor: 'pointer',
        transition: 'border-color 0.15s, color 0.15s',
        boxSizing: 'border-box',
        outline: 'none',
    },
    addBtnDisabled: {
        opacity: 0.35,
        cursor: 'not-allowed',
    },
    card: {
        background: '#1a1a1a',
        border: '1px solid #2a2a2a',
        borderRadius: '6px',
        padding: '10px 10px 12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
    },
    cardHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    previewBox: {
        width: '36px',
        height: '36px',
        borderRadius: '3px',
        background: '#111',
        border: '1px solid #333',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '14px',
        fontWeight: 400,
        fontFamily: "'JerseyM54', Arial, sans-serif",
        overflow: 'hidden',
    },
    cardTitle: {
        flex: 1,
        fontSize: '11px',
        color: '#ccc',
    },
    removeBtn: {
        all: 'unset',
        color: '#555',
        cursor: 'pointer',
        fontSize: '14px',
        lineHeight: 1,
        padding: '2px 4px',
        borderRadius: '3px',
        transition: 'color 0.12s',
        flexShrink: 0,
    },
    divider: {
        borderTop: '1px solid #2a2a2a',
    },
    controlRow: {
        display: 'flex',
        flexDirection: 'column',
        gap: '3px',
    },
    controlLabel: {
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: '10px',
        color: '#666',
    },
    controlValue: {
        color: '#999',
    },
    slider: {
        width: '100%',
        cursor: 'pointer',
        accentColor: '#ecff19',
    },
    textInput: {
        width: '100%',
        background: '#111',
        border: '1px solid #333',
        borderRadius: '4px',
        color: '#f8f9fa',
        fontSize: '18px',
        fontWeight: 400,
        fontFamily: "'JerseyM54', Arial, sans-serif",
        textAlign: 'center',
        padding: '8px',
        letterSpacing: '1px',
        outline: 'none',
        boxSizing: 'border-box',
    },
    colorTabRow: {
        display: 'flex',
        gap: '6px',
    },
    colorTab: (isActive) => ({
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '5px 8px',
        background: isActive ? '#2a2a2a' : '#111',
        border: isActive ? '1px solid #ecff19' : '1px solid #333',
        borderRadius: '4px',
        cursor: 'pointer',
        boxSizing: 'border-box',
    }),
    colorTabDot: {
        width: '14px',
        height: '14px',
        borderRadius: '3px',
        border: '1px solid #555',
        flexShrink: 0,
    },
    colorTabLabel: {
        fontSize: '10px',
        color: '#888',
    },
    hint: {
        fontSize: '10px',
        color: '#444',
        lineHeight: 1.5,
    },
};

// ── Per-card component so it can hold its own activeColorField state ──────────
class NameCard extends Component {
    state = { activeColorField: 'fill' };

    render() {
        const { name, onRemove, onUpdate } = this.props;
        const { activeColorField } = this.state;

        const activeColor = activeColorField === 'fill' ? name.fillColor : name.outlineColor;

        return (
            <div style={styles.card}>

                {/* Header */}
                <div style={styles.cardHeader}>
                    <div style={{
                        ...styles.previewBox,
                        color: name.fillColor,
                        WebkitTextStroke: `2px ${name.outlineColor}`,
                        textShadow: 'none',
                    }}>
                        {name.text || 'NAME'}
                    </div>
                    <span style={styles.cardTitle}>Name / Word</span>
                    <button style={styles.removeBtn} onClick={() => onRemove(name.id)} title="Remove name">✕</button>
                </div>

                <div style={styles.divider} />

                {/* Text input */}
                <div style={styles.controlRow}>
                    <div style={styles.controlLabel}><span>Text</span></div>
                    <input
                        type="text"
                        maxLength={20}
                        value={name.text}
                        style={styles.textInput}
                        placeholder="Enter name or word"
                        onChange={(e) => {
                            onUpdate(name.id, { text: e.target.value });
                        }}
                    />
                </div>

                {/* Fill / Outline tab selector */}
                <div style={styles.colorTabRow}>
                    {[
                        { field: 'fill',    label: 'Fill',    color: name.fillColor },
                        { field: 'outline', label: 'Outline', color: name.outlineColor },
                    ].map(({ field, label, color }) => (
                        <button
                            key={field}
                            style={styles.colorTab(activeColorField === field)}
                            onClick={() => this.setState({ activeColorField: field })}
                        >
                            <span style={{ ...styles.colorTabDot, background: color }} />
                            <span style={styles.colorTabLabel}>{label}</span>
                        </button>
                    ))}
                </div>

                {/* Athletic colour chart — operates on the active tab's colour */}
                <AthleticColorChart
                    selectedColor={activeColor}
                    onSelect={(hex) => {
                        onUpdate(name.id, activeColorField === 'fill'
                            ? { fillColor: hex }
                            : { outlineColor: hex }
                        );
                    }}
                />

                <div style={styles.divider} />

                {/* Outline width */}
                <div style={styles.controlRow}>
                    <div style={styles.controlLabel}>
                        <span>Outline Width</span>
                        <span style={styles.controlValue}>{name.outlineWidth}px</span>
                    </div>
                    <input type="range" min="0" max="80" step="1"
                        value={name.outlineWidth} style={styles.slider}
                        onChange={(e) => onUpdate(name.id, { outlineWidth: Number(e.target.value) })} />
                </div>

                <div style={styles.divider} />

                {/* X */}
                <div style={styles.controlRow}>
                    <div style={styles.controlLabel}>
                        <span>X Position</span>
                        <span style={styles.controlValue}>{Math.round(name.x)}</span>
                    </div>
                    <input type="range" min="0" max="1650" step="1"
                        value={name.x} style={styles.slider}
                        onChange={(e) => onUpdate(name.id, { x: Number(e.target.value) })} />
                </div>

                {/* Y */}
                <div style={styles.controlRow}>
                    <div style={styles.controlLabel}>
                        <span>Y Position</span>
                        <span style={styles.controlValue}>{Math.round(name.y)}</span>
                    </div>
                    <input type="range" min="0" max="1550" step="1"
                        value={name.y} style={styles.slider}
                        onChange={(e) => onUpdate(name.id, { y: Number(e.target.value) })} />
                </div>

                {/* Scale */}
                <div style={styles.controlRow}>
                    <div style={styles.controlLabel}>
                        <span>Scale</span>
                        <span style={styles.controlValue}>{name.scale.toFixed(2)}×</span>
                    </div>
                    <input type="range" min="0.1" max="5" step="0.05"
                        value={name.scale} style={styles.slider}
                        onChange={(e) => onUpdate(name.id, { scale: Number(e.target.value) })} />
                </div>

                {/* Rotate */}
                <div style={styles.controlRow}>
                    <div style={styles.controlLabel}>
                        <span>Rotate</span>
                        <span style={styles.controlValue}>{Math.round(name.rotate)}°</span>
                    </div>
                    <input type="range" min="-180" max="180" step="1"
                        value={name.rotate} style={styles.slider}
                        onChange={(e) => onUpdate(name.id, { rotate: Number(e.target.value) })} />
                </div>

            </div>
        );
    }
}

/**
 * NamePlacer
 *
 * Props:
 *   names     – NameItem[]  { id, text, fillColor, outlineColor, outlineWidth, x, y, scale, rotate }
 *   onAdd     – (item) => void
 *   onRemove  – (id) => void
 *   onUpdate  – (id, patch) => void
 */
class NamePlacer extends Component {
    handleAdd = () => {
        const { names, onAdd } = this.props;
        if (names.length >= MAX_NAMES) return;
        onAdd({
            id: `name-${Date.now()}-${Math.random().toString(36).slice(2)}`,
            text: '',
            fillColor: '#ffffff',
            outlineColor: '#000000',
            outlineWidth: 20,
            x: 825,
            y: 900,
            scale: 1,
            rotate: 0,
        });
    };

    render() {
        const { names, onRemove, onUpdate } = this.props;
        const canAdd = names.length < MAX_NAMES;

        return (
            <div style={styles.root}>

                {/* ── Add button ── */}
                <div>
                    <div style={styles.sectionLabel}>Names ({names.length} / {MAX_NAMES})</div>
                    <button
                        style={{ ...styles.addBtn, ...(canAdd ? {} : styles.addBtnDisabled) }}
                        onClick={this.handleAdd}
                        disabled={!canAdd}
                        title={canAdd ? 'Add a name or word to the design' : `Maximum ${MAX_NAMES} names reached`}
                    >
                        + Add Name
                    </button>
                </div>

                {names.length === 0 && (
                    <p style={styles.hint}>
                        Add up to {MAX_NAMES} names or words.<br />
                        Choose fill and outline colours, then use the sliders to position each one.
                    </p>
                )}

                {names.map((name) => (
                    <NameCard
                        key={name.id}
                        name={name}
                        onRemove={onRemove}
                        onUpdate={onUpdate}
                    />
                ))}

            </div>
        );
    }
}

export default NamePlacer;
